import React, { useState } from 'react';
import ArbitrageCalculatorModal from '../ArbitrageCalculatorModal';
import PositionOverviewModal from '../PositionOverviewModal';
import UnstakingModal from '../UnstakingModal';
import GetNIcpModal from '../GetNIcpModal';
import GetNIcpV2Modal from '../GetNIcpV2Modal';
import NeuronDetailsModal from '../NeuronDetailsModal';
import StakingModalV2 from '../StakingModalV2';
import SwapModal from '../SwapModal';
import ReceiveICPModal from '../ReceiveICPModal';
import GrowthRedemptionsModal from '../GrowthRedemptionsModal';
import { MARKET_PRICES, PROTOCOL_CONSTANTS, calculateTokenValueUSD } from '../../constants/pricing';

const LiquidStakingPage = ({ profileData, navigate }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPositionOverviewModalOpen, setIsPositionOverviewModalOpen] = useState(false);
  const [isUnstakingModalOpen, setIsUnstakingModalOpen] = useState(false);
  const [isGetNIcpModalOpen, setIsGetNIcpModalOpen] = useState(false);
  const [isGetNIcpV2ModalOpen, setIsGetNIcpV2ModalOpen] = useState(false);
  const [isNeuronDetailsModalOpen, setIsNeuronDetailsModalOpen] = useState(false);
  const [isStakingModalOpen, setIsStakingModalOpen] = useState(false);
  const [isSwapModalOpen, setIsSwapModalOpen] = useState(false);
  const [isReceiveICPModalOpen, setIsReceiveICPModalOpen] = useState(false);
  const [isGrowthRedemptionsModalOpen, setIsGrowthRedemptionsModalOpen] = useState(false);
  const [selectedNeuron, setSelectedNeuron] = useState(null);
  const balances = profileData.balances || {};
  const hasICP = balances.ICP > 0;
  const hasNICP = balances.nICP > 0;
  
  const icpBalance = balances.ICP || 0;
  const nIcpBalance = balances.nICP || 0;
  
  // Calculate total value of tokens that can be swapped to nICP (excluding nICP itself)
  const swappableTokens = Object.keys(balances).filter(token => token !== 'nICP' && balances[token] > 0);
  const totalSwappableValue = swappableTokens.reduce((total, token) => {
    return total + calculateTokenValueUSD(token, balances[token]);
  }, 0);

  // Calculate metrics for header cards
  const currentAPY = PROTOCOL_CONSTANTS.STAKING_APY;

  // Get withdrawal requests from data model
  const withdrawalRequests = profileData.withdrawalRequests || [];

  // Calculate total locked nICP from withdrawal requests
  const totalLockedNICP = withdrawalRequests.reduce((total, request) => total + request.nIcpBurned, 0);
  
  // Calculate total nICP including locked funds
  const totalNIcpIncludingLocked = nIcpBalance + totalLockedNICP;
  
  // Calculate rewards: separate guaranteed (dissolving) vs potential (available)
  
  
  // Calculate annual earnings in ICP for lifetime earned display
  const annualEarningsICP = totalNIcpIncludingLocked * PROTOCOL_CONSTANTS.N_ICP_NAV_OVER_ICP_PROTOCOL * (PROTOCOL_CONSTANTS.STAKING_APY / 100);
  
  // Calculate earnings for different time periods based on APY
  const dailyEarningsUSD = totalNIcpIncludingLocked > 0 ? (totalNIcpIncludingLocked * MARKET_PRICES.nICP * (PROTOCOL_CONSTANTS.STAKING_APY / 100) / 365) : 0;
  const monthlyEarningsUSD = totalNIcpIncludingLocked > 0 ? (totalNIcpIncludingLocked * MARKET_PRICES.nICP * (PROTOCOL_CONSTANTS.STAKING_APY / 100) / 12) : 0;
  
  // Mock lifetime earnings data (could be based on actual historical data)
  const lifetimeRewardsUSD = totalNIcpIncludingLocked > 0 ? annualEarningsICP * MARKET_PRICES.ICP * 0.3 : 45.26; // 30% of annual as lifetime earned

  const handleNeuronClick = (neuron) => {
    setSelectedNeuron(neuron);
    setIsNeuronDetailsModalOpen(true);
  };


  // Determine which state to show based on user's tokens and withdrawal requests
  const getUserState = () => {
    if (hasNICP && withdrawalRequests.length > 0) return 'nICPWithWithdrawals';
    if (hasNICP) return 'nICPOnly';
    if (hasICP) return 'withICP';
    return 'noTokens';
  };

  const renderNoTokensState = () => (
    <div>
      {/* Empty state content can be added here if needed */}
    </div>
  );

  const renderWithICPState = () => (
    <div>
      {/* Content for users with ICP can be added here if needed */}
    </div>
  );

  const renderNICPOnlyState = () => (
    <div>
      {/* Content for users with nICP only (no withdrawal requests) can be added here if needed */}
    </div>
  );

  const renderNICPWithWithdrawalsState = () => (
    <div>
      {/* Content for users with nICP + withdrawal requests can be added here if needed */}
    </div>
  );

  const userState = getUserState();

  return (
    <div>
      <div className="staking-header">
        {/* Header with back arrow, icon, title, and Live APY */}
        <div className="header-top">
          <button 
            className="back-button"
            onClick={() => navigate('/earn')}
          >
            ←
          </button>
          <div className="header-center">
             <h1 className="header-title"> ICP Liquid Staking (WaterNeuron) </h1>
             <p className="header-subtitle">A liquid staking protocol that lets you earn staking rewards and protocol value while keeping liquidity for your ICP.</p>
          </div>
          <div className="header-right">
            <div className="apy-display">15%</div>
            <a 
              href="https://docs.oisy.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="learn-more-button"
            >
              Learn more
              <span className="external-link-icon">↗</span>
            </a>
          </div>
        </div>
        
        {/* Header Cards */}
        <div className="header-cards-container">
          {/* Top Row - Earnings Overview Widget - Only show for users with nICP stakes */}
          {totalNIcpIncludingLocked > 0 && (
            <div className="header-card-top">
              <div className="header-card">
                <div className="earnings-widget-content">
                  <div className="earnings-widget-left">
                  </div>
                  <div className="earnings-metrics">
                    <div className="earnings-metric">
                      <div className="earnings-metric-label">Lifetime rewards</div>
                      <div className="earnings-metric-value">${lifetimeRewardsUSD.toFixed(2)}</div>
                    </div>
                    <div className="earnings-metric">
                      <div className="earnings-metric-label">Last 24 hours</div>
                      <div className="earnings-metric-value">${dailyEarningsUSD.toFixed(2)}</div>
                    </div>
                    <div className="earnings-metric">
                      <div className="earnings-metric-label">This month</div>
                      <div className="earnings-metric-value">${monthlyEarningsUSD.toFixed(2)}</div>
                    </div>
                  </div>

                  <button 
                    className="earnings-view-details-button"
                    onClick={() => setIsGrowthRedemptionsModalOpen(true)}
                  >
                    View details
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bottom Row - ICP Available and Your nICP Cards */}
          <div className="header-cards-bottom">
            {/* ICP Available Card */}
            <div className="header-card">
              <div className="header-card-header">
                <div className="header-card-label">ICP Available</div>
                <button 
                  className={`header-card-receive-button ${icpBalance === 0 ? 'primary' : ''}`}
                  onClick={() => setIsReceiveICPModalOpen(true)}
                >
                  Receive
                </button>
              </div>
              <div className="header-card-value">{icpBalance.toFixed(0)} ICP</div>
              {icpBalance > 0 && (
                <div className="header-card-usd">${(icpBalance * MARKET_PRICES.ICP).toFixed(2)}</div>
              )}
              <div className="header-card-subtitle">
                {icpBalance > 0 ? (
                  <span className="warning-chip">⚠️ Missing out ${(icpBalance * MARKET_PRICES.ICP * (PROTOCOL_CONSTANTS.STAKING_APY / 100)).toFixed(0)}/year</span>
                ) : (
                  'Get ICP to start earning'
                )}
              </div>
              <button 
                className={`header-card-button-full ${icpBalance === 0 ? 'secondary' : ''}`}
                onClick={() => icpBalance > 0 ? setIsGetNIcpV2ModalOpen(true) : setIsReceiveICPModalOpen(true)}
              >
                Stake
              </button>
            </div>

            {/* Your nICP Card */}
            <div className="header-card nicp-earnings-card">
              <div className="header-card-label">Your nICP</div>
              {totalNIcpIncludingLocked > 0 ? (
                // Show nICP amount with delayed unstake projection
                <>
                  <div className="header-card-value">
                    {(totalLockedNICP > 0 ? nIcpBalance : totalNIcpIncludingLocked).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} nICP
                  </div>
                  {totalLockedNICP > 0 && (
                    <div className="withdrawing-amount">
                      {totalLockedNICP.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} nICP withdrawing
                    </div>
                  )}
                  <div className="header-card-usd">${(totalNIcpIncludingLocked * MARKET_PRICES.nICP).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
                  <div className="nicp-earning-subtitle">
                    Delayed unstake (6mo) ~{((totalLockedNICP > 0 ? nIcpBalance : totalNIcpIncludingLocked) * PROTOCOL_CONSTANTS.N_ICP_NAV_OVER_ICP_PROTOCOL * 1.075).toFixed(2)} ICP
                  </div>
                </>
              ) : (
                // Show example layout for new users
                <>
                  <div className="header-card-value">0 nICP</div>
                  <div className="nicp-earning-subtitle">
                    {icpBalance > 0 
                      ? `${icpBalance.toFixed(0)} ICP → ≈${(icpBalance * 1.075).toFixed(1)} ICP in 6 mo`
                      : 'Example: 100 ICP → 115 ICP in 1y'
                    }
                  </div>
                </>
              )}
              {totalNIcpIncludingLocked > 0 && (
                <button 
                  className="header-card-button-full secondary"
                  onClick={() => setIsUnstakingModalOpen(true)}
                >
                  Unstake / Swap
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* How it works section for users without nICP - positioned below header */}
      {userState !== 'nICPWithWithdrawals' && userState !== 'nICPOnly' && (
        <div className="info-section">
          <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>
            How it works 
          </h3>
          <div className="info-grid">
            <div className="info-item">
              <div className="info-icon">🔄</div>
              <div>
                <h4>Liquid Staking</h4>
                <p>Convert ICP to nICP and keep earning rewards while maintaining liquidity</p>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">💰</div>
              <div>
                <h4>High APY</h4>
                <p>Earn up to 15% yearly rewards on your staked ICP tokens</p>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">⚡</div>
              <div>
                <h4>Instant Liquidity</h4>
                <p>Use your nICP tokens in DeFi while still earning staking rewards</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="liquid-staking-container">
        {/* Main content based on user state */}
        {userState === 'noTokens' && renderNoTokensState()}
        {userState === 'withICP' && renderWithICPState()}
        {userState === 'nICPOnly' && renderNICPOnlyState()}
        {userState === 'nICPWithWithdrawals' && renderNICPWithWithdrawalsState()}

        {/* Available to Swap Section - Show before Staked Positions for users without nICP */}
        {swappableTokens.length > 0 && (userState === 'noTokens' || userState === 'withICP') && (
          <div className="available-to-swap">
            <div className="swap-header">
              <div className="swap-header-icon">
                <span className="swap-icon">🔄</span>
                <span className="dollar-icon">💰</span>
              </div>
            <div className="swap-header-text">
              <h2>You have ${totalSwappableValue.toFixed(0)} in tokens not earning anything</h2>
              <p>Swap to nICP and start earning {PROTOCOL_CONSTANTS.STAKING_APY}% APY!</p>
            </div>
            </div>
            
            <div className="swap-table">
              <div className="swap-table-header">
                <div className="swap-table-cell header-cell">Asset</div>
                <div className="swap-table-cell header-cell">Amount</div>
                <div className="swap-table-cell header-cell">Est. rewards</div>
                <div className="swap-table-cell header-cell"></div>
              </div>
              
              {swappableTokens.map(token => {
                const tokenAmount = balances[token];
                const tokenValueUSD = calculateTokenValueUSD(token, tokenAmount);
                const estimatedRewardsUSD = tokenValueUSD * (PROTOCOL_CONSTANTS.STAKING_APY / 100);
                
                return (
                  <div key={token} className="swap-table-row">
                    <div className="swap-table-cell asset-cell">
                      <div className="asset-info">
                        <div className="asset-icon">
                          {token === 'ICP' && '♾️'}
                          {token === 'ckBTC' && '₿'}
                          {token === 'ETH' && 'Ξ'}
                          {token === 'ckUSDT' && '💵'}
                          {token === 'GLDT' && '🥇'}
                          {token === 'PANDA' && '🐼'}
                        </div>
                        <div className="asset-details">
                          <div className="asset-name">{token}</div>
                          <div className="asset-description">
                            {token === 'ICP' && 'Internet Computer'}
                            {token === 'ckBTC' && 'Chain-key Bitcoin'}
                            {token === 'ETH' && 'Ethereum'}
                            {token === 'ckUSDT' && 'Chain-key USDT'}
                            {token === 'GLDT' && 'Gold DAO Token'}
                            {token === 'PANDA' && 'Panda DAO'}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="swap-table-cell amount-cell">
                      <div className="amount-value">{tokenAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      <div className="amount-usd">${tokenValueUSD.toFixed(2)}</div>
                    </div>
                    
                    <div className="swap-table-cell rewards-cell">
                      <div className="rewards-value">${estimatedRewardsUSD.toFixed(2)}</div>
                      <div className="rewards-apy">APY {PROTOCOL_CONSTANTS.STAKING_APY}%</div>
                    </div>
                    
                    <div className="swap-table-cell action-cell">
                      <button 
                        className="swap-action-button"
                        onClick={() => setIsSwapModalOpen(true)}
                      >
                        Swap to nICP
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Staked Positions - Show for all users */}
        <div className="staked-positions">
          <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
            Staked Positions
          </h2>

          {!hasNICP ? (
            /* Empty State */
            <div className="empty-positions">
              <div className="empty-message">
                <div className="empty-icon">🏦</div>
                <h3>No staked positions yet</h3>
                <p>Start by getting nICP to see your positions here.</p>
              </div>
            </div>
          ) : (
            <>
              {/* Liquid Positions Section */}
              <div className="positions-section">
                <h3 className="section-title">💧 Liquid Positions</h3>
                <div className="positions-table liquid-overview">
                  <div className="table-header">
                    <div className="header-cell">Asset</div>
                    <div className="header-cell">APY</div>
                    <div className="header-cell">6-Month Value</div>
                  </div>
                  <div 
                    className="table-row clickable"
                    onClick={() => setIsPositionOverviewModalOpen(true)}
                    title="Click to view unstaking options and detailed overview"
                  >
                    <div className="table-cell">
                      <div className="asset-info">
                        <span className="asset-icon">💧</span>
                        <div className="asset-details">
                          <span className="asset-name">{nIcpBalance.toFixed(0)} nICP</span>
                          <span className="asset-value">${(nIcpBalance * MARKET_PRICES.nICP).toFixed(0)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="table-cell">
                      <span className="apy-value green">{currentAPY}%</span>
                    </div>
                    <div className="table-cell">
                      <div className="unstake-value">
                        <span className="unstake-amount">~{(nIcpBalance * PROTOCOL_CONSTANTS.N_ICP_NAV_OVER_ICP_PROTOCOL).toFixed(0)} ICP</span>
                        <span className="unstake-usd">${((nIcpBalance * PROTOCOL_CONSTANTS.N_ICP_NAV_OVER_ICP_PROTOCOL) * MARKET_PRICES.ICP).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                        <span className="unstake-timeframe">in 6 months</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Withdrawal Requests Section */}
              {withdrawalRequests.length > 0 && (
                <div className="positions-section">
                  <h3 className="section-title">🔄 Withdrawal Requests</h3>
                  <div className="positions-table withdrawing-simplified">
                    <div className="table-header">
                      <div className="header-cell">nICP Burned</div>
                      <div className="header-cell">ICP Due</div>
                      <div className="header-cell">Status</div>
                    </div>
                    {withdrawalRequests.map((request) => (
                      <div 
                        key={request.id}
                        className="table-row clickable"
                        onClick={() => handleNeuronClick(request)}
                      >
                        <div className="table-cell">
                          <div className="amount-with-usd">
                            <span className="burned-amount">{request.nIcpBurned.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                            <span className="usd-value">${(request.nIcpBurned * MARKET_PRICES.nICP).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        </div>
                        <div className="table-cell">
                          <div className="amount-with-usd">
                            <span className="due-amount">{request.icpDue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                            <span className="usd-value">${(request.icpDue * MARKET_PRICES.ICP).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        </div>
                        <div className="table-cell">
                          <div className="status-with-timeline">
                            <span className="status-badge waiting">{request.status}</span>
                            <div className="dissolution-timeline">
                              <span className="days-left">{request.daysLeft} days left</span>
                              <span className="start-date">Started {request.startDate}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Available to Swap Section - Show after Staked Positions for users with nICP */}
        {swappableTokens.length > 0 && (userState === 'nICPWithWithdrawals' || userState === 'nICPOnly') && (
          <div className="available-to-swap">
            <div className="swap-header">
              <div className="swap-header-icon">
                <span className="swap-icon">🔄</span>
                <span className="dollar-icon">💰</span>
              </div>
            <div className="swap-header-text">
              <h2>You have ${totalSwappableValue.toFixed(0)} in tokens not earning anything</h2>
              <p>Swap to nICP and start earning {PROTOCOL_CONSTANTS.STAKING_APY}% APY!</p>
            </div>
            </div>
            
            <div className="swap-table">
              <div className="swap-table-header">
                <div className="swap-table-cell header-cell">Asset</div>
                <div className="swap-table-cell header-cell">Amount</div>
                <div className="swap-table-cell header-cell">Est. rewards</div>
                <div className="swap-table-cell header-cell"></div>
              </div>
              
              {swappableTokens.map(token => {
                const tokenAmount = balances[token];
                const tokenValueUSD = calculateTokenValueUSD(token, tokenAmount);
                const estimatedRewardsUSD = tokenValueUSD * (PROTOCOL_CONSTANTS.STAKING_APY / 100);
                
                return (
                  <div key={token} className="swap-table-row">
                    <div className="swap-table-cell asset-cell">
                      <div className="asset-info">
                        <div className="asset-icon">
                          {token === 'ICP' && '♾️'}
                          {token === 'ckBTC' && '₿'}
                          {token === 'ETH' && 'Ξ'}
                          {token === 'ckUSDT' && '💵'}
                          {token === 'GLDT' && '🥇'}
                          {token === 'PANDA' && '🐼'}
                        </div>
                        <div className="asset-details">
                          <div className="asset-name">{token}</div>
                          <div className="asset-description">
                            {token === 'ICP' && 'Internet Computer'}
                            {token === 'ckBTC' && 'Chain-key Bitcoin'}
                            {token === 'ETH' && 'Ethereum'}
                            {token === 'ckUSDT' && 'Chain-key USDT'}
                            {token === 'GLDT' && 'Gold DAO Token'}
                            {token === 'PANDA' && 'Panda DAO'}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="swap-table-cell amount-cell">
                      <div className="amount-value">{tokenAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      <div className="amount-usd">${tokenValueUSD.toFixed(2)}</div>
                    </div>
                    
                    <div className="swap-table-cell rewards-cell">
                      <div className="rewards-value">${estimatedRewardsUSD.toFixed(2)}</div>
                      <div className="rewards-apy">APY {PROTOCOL_CONSTANTS.STAKING_APY}%</div>
                    </div>
                    
                    <div className="swap-table-cell action-cell">
                      <button 
                        className="swap-action-button"
                        onClick={() => setIsSwapModalOpen(true)}
                      >
                        Swap to nICP
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}


        {/* Activity Section - Show only for users with nICP */}
        {(userState === 'nICPWithWithdrawals' || userState === 'nICPOnly') && (
          <div className="activity-section">
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
              Activity
            </h2>
            <div className="activity-list">
              <div className="activity-item">
                <div className="activity-icon disbursed">💰</div>
                <div className="activity-content">
                  <div className="activity-title">Disbursed 🔗</div>
                  <div className="activity-subtitle">To your OISY Wallet </div>
                </div>
                <div className="activity-amount">
                  <div className="amount positive">+85.32 ICP</div>
                  <div className="activity-date">12 Jul 2025, 08:50</div>
                </div>
              </div>
              
              <div className="activity-item">
                <div className="activity-icon received">⬇</div>
                <div className="activity-content">
                  <div className="activity-title">Received 🔗</div>
                  <div className="activity-subtitle">From kdsfdf-...-cai </div>
                </div>
                <div className="activity-amount">
                  <div className="amount positive">+150 nICP</div>
                  <div className="activity-date">8 Jul 2025, 12:24</div>
                </div>
              </div>
              
              <div className="activity-item">
                <div className="activity-icon unstaked">⊖</div>
                <div className="activity-content">
                  <div className="activity-title">Unstaked 🔗</div>
                  <div className="activity-subtitle">From Test Wallet • ICP</div>
                </div>
                <div className="activity-amount">
                  <div className="amount negative">-75 nICP</div>
                  <div className="activity-date">7 Jul 2025, 21:45</div>
                </div>
              </div>
              
              <div className="activity-item">
                <div className="activity-icon received">⬇</div>
                <div className="activity-content">
                  <div className="activity-title">Received 🔗</div>
                  <div className="activity-subtitle">From kdsfdf-...-cai </div>
                </div>
                <div className="activity-amount">
                  <div className="amount positive">+100 nICP</div>
                  <div className="activity-date">7 Jul 2025, 09:54</div>
                </div>
              </div>
            </div>
            
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button className="show-more-btn">
                📋 Show full history
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Testing Section - Get nICP buttons moved to bottom */}
      <div className="testing-section" style={{ 
        marginTop: '40px', 
        padding: '20px', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '12px',
        border: '1px dashed #dee2e6'
      }}>
        <h3 style={{ 
          fontSize: '14px', 
          color: '#6c757d', 
          marginBottom: '16px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          fontWeight: '600'
        }}>
          Testing Only
        </h3>
        <div className="get-nicp-buttons">
          <button 
            className="get-nicp-button"
            onClick={() => setIsGetNIcpModalOpen(true)}
          >
            <span>💧</span>
            Get nICP
          </button>
          <button 
            className="get-nicp-button secondary"
            onClick={() => setIsGetNIcpV2ModalOpen(true)}
          >
            <span>💧</span>
            Stake ICP 
          </button>
        </div>
      </div>

      <ArbitrageCalculatorModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      <PositionOverviewModal
        isOpen={isPositionOverviewModalOpen}
        onClose={() => setIsPositionOverviewModalOpen(false)}
        userNICP={nIcpBalance}
      />

      <UnstakingModal
        isOpen={isUnstakingModalOpen}
        onClose={() => setIsUnstakingModalOpen(false)}
        userNICP={nIcpBalance}
      />

      <GetNIcpModal 
        isOpen={isGetNIcpModalOpen}
        onClose={() => setIsGetNIcpModalOpen(false)}
        userICP={icpBalance}
      />

      <GetNIcpV2Modal 
        isOpen={isGetNIcpV2ModalOpen}
        onClose={() => setIsGetNIcpV2ModalOpen(false)}
        userICP={icpBalance}
      />

      <NeuronDetailsModal
        isOpen={isNeuronDetailsModalOpen}
        onClose={() => setIsNeuronDetailsModalOpen(false)}
        neuronData={selectedNeuron}
      />

      <StakingModalV2
        isOpen={isStakingModalOpen}
        onClose={() => setIsStakingModalOpen(false)}
        userICP={icpBalance}
        balances={balances}
      />

      <SwapModal
        isOpen={isSwapModalOpen}
        onClose={() => setIsSwapModalOpen(false)}
        defaultFromToken="ICP"
        defaultToToken="nICP"
        balances={balances}
      />

      <ReceiveICPModal
        isOpen={isReceiveICPModalOpen}
        onClose={() => setIsReceiveICPModalOpen(false)}
      />

      <GrowthRedemptionsModal
        isOpen={isGrowthRedemptionsModalOpen}
        onClose={() => setIsGrowthRedemptionsModalOpen(false)}
        profileData={profileData}
      />
    </div>
  );
};

export default LiquidStakingPage;
